import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  Calendar,
  Scan,
  BookOpen,
  MapPin,
  Receipt,
  Settings,
  HelpCircle,
  X,
  LucideIcon,
} from "lucide-react";
import styles from "./NavigationMenu.module.scss";

interface NavigationMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  const menuItems: MenuItem[] = [
    { path: "/", label: t("home"), icon: Home },
    { path: "/calendar", label: t("calendar"), icon: Calendar },
    { path: "/scan", label: t("scan"), icon: Scan },
    { path: "/az-guide", label: t("az_guide"), icon: BookOpen },
    { path: "/locations", label: t("locations"), icon: MapPin },
    { path: "/fees", label: t("fees_sales_points"), icon: Receipt },
    { path: "/settings", label: t("settings"), icon: Settings },
  ];

  const handleOverlayClick = () => {
    onClose();
  };

  const handleDrawerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  const hiddenTabIndex = isOpen ? undefined : -1;

  return (
    <div
      className={`${styles["nav-menu"]} ${isOpen ? styles["nav-menu--open"] : ""}`}
      onClick={handleOverlayClick}
      aria-hidden={!isOpen}
    >
      <div className={styles["nav-menu__drawer"]} onClick={handleDrawerClick}>
        <div className={styles["nav-menu__header"]}>
          <h2 className={styles["nav-menu__title"]}>Sortsofort</h2>
          <button
            className={styles["nav-menu__close-btn"]}
            onClick={onClose}
            aria-label={t("a11y_close_menu")}
            tabIndex={hiddenTabIndex}
          >
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        <nav className={styles["nav-menu__list"]}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `${styles["nav-menu__item"]} ${isActive ? styles["nav-menu__item--active"] : ""}`
                }
                onClick={onClose}
                tabIndex={hiddenTabIndex}
              >
                <Icon
                  className={styles["nav-menu__item-icon"]}
                  size={20}
                  aria-hidden="true"
                />
                <span className={styles["nav-menu__item-label"]}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </nav>

        <div className={styles["nav-menu__footer"]}>
          <NavLink
            to="/help"
            className={styles["nav-menu__footer-link"]}
            onClick={onClose}
            tabIndex={hiddenTabIndex}
          >
            <HelpCircle
              className={styles["nav-menu__item-icon"]}
              size={20}
              aria-hidden="true"
            />
            <span>{t("help_support")}</span>
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default NavigationMenu;
